import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, useGradedSlips } from '../api/hooks';
import './EntriesPage.css';

function formatTs(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatSportLeague(sport, league) {
  const parts = [sport, league].filter(Boolean);
  return parts.length > 0 ? parts.join(':') : '—';
}

function formatDiscountType(value) {
  return String(value || '').replaceAll('_', ' ');
}

function formatBoost(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${(Number(value) * 100).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}%`;
}

function formatBoostSummary(boost, gradedBoost) {
  if (boost == null && gradedBoost == null) return '—';
  const configured = formatBoost(boost);
  const graded = gradedBoost == null ? null : formatMoney(gradedBoost);
  return [configured === '—' ? null : configured, graded ? `${graded} graded` : null]
    .filter(Boolean)
    .join(' · ');
}

function statusClass(status) {
  if (status === 'won') return 'entries-status-applied';
  if (status === 'lost') return 'entries-status-canceled';
  if (status === 'pending') return 'entries-status-pending';
  return 'entries-status-other';
}

const EMPTY_LEG_FILTERS = {
  player: '',
  position: '',
  team: '',
  market: '',
  side: 'all',
  line: '',
  result: '',
  sportLeague: '',
  provider: '',
};

function GradedSlipsPage() {
  const queryClient = useQueryClient();
  const [view, setView] = useState('events');
  const [minutes, setMinutes] = useState(360);
  const [status, setStatus] = useState('all');
  const [fullyGraded, setFullyGraded] = useState('all');
  const [combo, setCombo] = useState('all');
  const [ezPick, setEzPick] = useState('all');
  const [numLegs, setNumLegs] = useState('all');
  const [discount, setDiscount] = useState('all');
  const [sortBy, setSortBy] = useState('published');
  const [draftParlayId, setDraftParlayId] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [draftLegFilters, setDraftLegFilters] = useState(EMPTY_LEG_FILTERS);
  const [filters, setFilters] = useState({
    parlayId: '',
    userId: '',
    ...EMPTY_LEG_FILTERS,
  });
  const [expanded, setExpanded] = useState(() => new Set());

  const queryParams = useMemo(() => ({
    view,
    minutes,
    limit: 500,
    status: status === 'all' ? null : status,
    fully_graded: fullyGraded === 'all' ? null : fullyGraded === 'yes',
    combo: combo === 'all' ? null : combo === 'yes',
    ez_pick: ezPick === 'all' ? null : ezPick === 'yes',
    num_legs: numLegs === 'all' ? null : Number(numLegs),
    discount: discount === 'all' ? null : discount,
    sort_by: sortBy,
    parlay_id: filters.parlayId || null,
    user_id: filters.userId || null,
    player: filters.player || null,
    position: filters.position || null,
    team: filters.team || null,
    market: filters.market || null,
    side: filters.side === 'all' ? null : filters.side,
    line: filters.line || null,
    result: filters.result || null,
    sport: filters.sportLeague.split(':')[0] || null,
    league: filters.sportLeague.includes(':')
      ? filters.sportLeague.split(':').slice(1).join(':') || null
      : null,
    provider: filters.provider || null,
  }), [
    view, minutes, status, fullyGraded, combo, ezPick, numLegs, discount, sortBy, filters,
  ]);

  const { data, isLoading, isError, error, isFetching } = useGradedSlips(queryParams);
  const rows = Array.isArray(data) ? data : [];

  const applyFilters = useCallback(() => {
    setFilters({
      parlayId: draftParlayId.trim(),
      userId: draftUserId.trim(),
      player: draftLegFilters.player.trim(),
      position: draftLegFilters.position.trim(),
      team: draftLegFilters.team.trim(),
      market: draftLegFilters.market.trim(),
      side: draftLegFilters.side,
      line: draftLegFilters.line.trim(),
      result: draftLegFilters.result.trim(),
      sportLeague: draftLegFilters.sportLeague.trim(),
      provider: draftLegFilters.provider.trim(),
    });
  }, [draftParlayId, draftUserId, draftLegFilters]);

  const clearFilters = useCallback(() => {
    setDraftParlayId('');
    setDraftUserId('');
    setDraftLegFilters(EMPTY_LEG_FILTERS);
    setFilters({ parlayId: '', userId: '', ...EMPTY_LEG_FILTERS });
    setStatus('all');
    setFullyGraded('all');
    setCombo('all');
    setEzPick('all');
    setNumLegs('all');
    setDiscount('all');
    setSortBy('published');
  }, []);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.gradedSlips(queryParams) });
  }, [queryClient, queryParams]);

  const toggleExpanded = useCallback((messageId) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }, []);

  return (
    <div className="entries-page">
      <div className="page-header">
        <div>
          <h1>Entries · Graded slips</h1>
          <p className="subtle" style={{ margin: '0.35rem 0 0' }}>
            Read-only Pub/Sub snapshots. These events do not enter exposure, replication, or KF.
          </p>
        </div>
        <div className="header-actions">
          <span className="subtle">{isFetching ? 'Refreshing…' : `${rows.length} rows`}</span>
          <button type="button" className="button" onClick={refresh}>Refresh</button>
        </div>
      </div>

      <div className="card filter-bar entries-filter-bar" style={{ marginBottom: '1rem' }}>
        <div className="entries-filter-row">
          <label className="entries-filter-label">
            View
            <select value={view} onChange={(event) => setView(event.target.value)}>
              <option value="events">Every event</option>
              <option value="latest">Latest per slip</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Window
            <select value={minutes} onChange={(event) => setMinutes(Number(event.target.value))}>
              <option value={60}>1 hour</option>
              <option value={360}>6 hours</option>
              <option value={1440}>24 hours</option>
              <option value={4320}>3 days</option>
              <option value={0}>7 days</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">All</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Fully graded
            <select value={fullyGraded} onChange={(event) => setFullyGraded(event.target.value)}>
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Combo
            <select value={combo} onChange={(event) => setCombo(event.target.value)}>
              <option value="all">All</option>
              <option value="yes">Combo only</option>
              <option value="no">No combo</option>
            </select>
          </label>
          <label className="entries-filter-label">
            EZPicks
            <select value={ezPick} onChange={(event) => setEzPick(event.target.value)}>
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Legs
            <select value={numLegs} onChange={(event) => setNumLegs(event.target.value)}>
              <option value="all">All</option>
              {Array.from({ length: 20 }, (_, index) => index + 1).map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>
          <label className="entries-filter-label">
            Discount
            <select value={discount} onChange={(event) => setDiscount(event.target.value)}>
              <option value="all">All</option>
              <option value="any">Any discount</option>
              <option value="none">No discount</option>
              <option value="profit_boost">Profit boost</option>
              <option value="protected_pick">Protected pick</option>
              <option value="free_picks_entry">Free picks entry</option>
              <option value="line_discount">Line discount</option>
              <option value="over_boost">Over boost</option>
              <option value="under_boost">Under boost</option>
              <option value="payout_boost">Payout boost</option>
              <option value="protected_pick_generator">Protected pick generator</option>
              <option value="free_picks_entry_generator">Free picks generator</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Sort
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="published">Newest</option>
              <option value="combo">Combo first</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Parlay ID
            <input
              className="entries-filter-input-wide"
              value={draftParlayId}
              onChange={(event) => setDraftParlayId(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            User ID
            <input
              className="entries-filter-input-user"
              value={draftUserId}
              onChange={(event) => setDraftUserId(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
        </div>
        <div className="entries-filter-row" style={{ marginTop: '0.65rem' }}>
          <label className="entries-filter-label">
            Player
            <input
              value={draftLegFilters.player}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, player: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            Position
            <input
              value={draftLegFilters.position}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, position: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            Team
            <input
              value={draftLegFilters.team}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, team: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            Market
            <input
              value={draftLegFilters.market}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, market: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            Side
            <select
              value={draftLegFilters.side}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, side: event.target.value,
              }))}
            >
              <option value="all">All</option>
              <option value="over">Over</option>
              <option value="under">Under</option>
            </select>
          </label>
          <label className="entries-filter-label">
            Line
            <input
              value={draftLegFilters.line}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, line: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            Result
            <input
              value={draftLegFilters.result}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, result: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            Sport / league
            <input
              value={draftLegFilters.sportLeague}
              placeholder="soccer:mls"
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, sportLeague: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <label className="entries-filter-label">
            Provider
            <input
              value={draftLegFilters.provider}
              onChange={(event) => setDraftLegFilters((current) => ({
                ...current, provider: event.target.value,
              }))}
              onKeyDown={(event) => { if (event.key === 'Enter') applyFilters(); }}
            />
          </label>
          <button type="button" className="button button-small" onClick={applyFilters}>Search</button>
          <button type="button" className="button button-small" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      <div className="card entries-table-card">
        {isLoading ? (
          <div className="loading">Loading graded slips…</div>
        ) : isError ? (
          <div className="loading">{error?.message || 'Failed to load graded slips'}</div>
        ) : rows.length === 0 ? (
          <div className="loading">No graded snapshots match these filters.</div>
        ) : (
          <div className="entries-table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th aria-label="Expand leg details" />
                  <th>Entered</th>
                  <th>Published</th>
                  <th>Status</th>
                  <th>Combo</th>
                  <th>EZPicks</th>
                  <th>Legs</th>
                  <th>Discounts</th>
                  <th>Boost</th>
                  <th>Cashback</th>
                  <th>Parlay</th>
                  <th>User</th>
                  <th>Leg progress</th>
                  <th>Fully graded</th>
                  <th>Fee</th>
                  <th>Graded payout</th>
                  <th>Graded at</th>
                  <th>Message ID</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const terminal = Number(row.selected_won_legs || 0)
                    + Number(row.selected_lost_legs || 0)
                    + Number(row.selected_void_legs || 0);
                  const isExpanded = expanded.has(row.message_id);
                  const legs = Array.isArray(row.legs) ? row.legs : [];
                  return (
                    <React.Fragment key={row.message_id}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className={`graded-expand-button${isExpanded ? ' is-expanded' : ''}`}
                            onClick={() => toggleExpanded(row.message_id)}
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} legs for parlay ${row.parlay_id}`}
                            title={`${isExpanded ? 'Collapse' : 'Expand'} leg details`}
                          >
                            <span aria-hidden="true">›</span>
                          </button>
                        </td>
                        <td>{formatTs(row.created_ts)}</td>
                        <td>{formatTs(row.publish_ts)}</td>
                        <td>
                          <span className={`entries-status-pill ${statusClass(row.parlay_status)}`}>
                            {row.parlay_status || '—'}
                          </span>
                        </td>
                        <td>
                          {row.is_combo ? (
                            <span className="entries-status-pill entries-status-other">combo</span>
                          ) : '—'}
                        </td>
                        <td>{row.is_ez_pick ? 'yes' : 'no'}</td>
                        <td>{row.selected_num_legs ?? 0}</td>
                        <td>
                          {Array.isArray(row.discount_types) && row.discount_types.length > 0
                            ? row.discount_types.map(formatDiscountType).join(', ')
                            : '—'}
                        </td>
                        <td>{formatBoostSummary(row.profit_boost, row.graded_profit_boost)}</td>
                        <td>{formatMoney(row.cashback_amount)}</td>
                        <td><code>{row.parlay_id || '—'}</code></td>
                        <td><code>{row.user_id || '—'}</code></td>
                        <td title={`${row.selected_won_legs || 0} won · ${row.selected_lost_legs || 0} lost · ${row.selected_pending_legs || 0} pending`}>
                          {terminal}/{row.selected_num_legs || 0}
                          {Number(row.selected_pending_legs || 0) > 0 ? ` (${row.selected_pending_legs} pending)` : ''}
                        </td>
                        <td>{row.selected_fully_graded ? 'yes' : 'no'}</td>
                        <td>{formatMoney(row.currency_amount)}</td>
                        <td>{formatMoney(row.graded_payout)}</td>
                        <td>{formatTs(row.graded_ts)}</td>
                        <td><code>{row.message_id || '—'}</code></td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={18} style={{ padding: '0.65rem 1rem' }}>
                            {legs.length === 0 ? (
                              <span className="subtle">No leg details in this snapshot.</span>
                            ) : (
                              <>
                                {row.is_combo && row.combo_details_available === false && (
                                  <div className="subtle" style={{ marginBottom: '0.5rem' }}>
                                    Combo team, market, line, and market result are not included
                                    in the Pub/Sub payload.
                                  </div>
                                )}
                                <div className="entries-table-wrap">
                                  <table className="table">
                                  <thead>
                                    <tr>
                                      <th>Status</th>
                                      <th>Player</th>
                                      <th>Position</th>
                                      <th>Team</th>
                                      <th>Market</th>
                                      <th>Side</th>
                                      <th>Line</th>
                                      <th>Result</th>
                                      <th>Sport / league</th>
                                      <th>Provider</th>
                                      <th>Discount</th>
                                      <th>Game</th>
                                      <th>Leg ID</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {legs.map((leg, index) => (
                                      <tr key={leg.leg_id || `${row.message_id}-${index}`}>
                                        <td>
                                          <span className={`entries-status-pill ${statusClass(leg.status)}`}>
                                            {leg.status || '—'}
                                          </span>
                                        </td>
                                        <td>{leg.player_name || leg.subject_id || '—'}</td>
                                        <td>{leg.position || '—'}</td>
                                        <td>{leg.team || '—'}</td>
                                        <td>{leg.wager_type || leg.market_type || '—'}</td>
                                        <td>{leg.outcome || '—'}</td>
                                        <td>{leg.outcome_value ?? '—'}</td>
                                        <td>{leg.final_value ?? '—'}</td>
                                        <td>{formatSportLeague(leg.sport, leg.league)}</td>
                                        <td>{leg.provider || '—'}</td>
                                        <td>{formatDiscountType(leg.promotion_type) || '—'}</td>
                                        <td><code>{leg.game_id || '—'}</code></td>
                                        <td><code>{leg.leg_id || '—'}</code></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="entries-footer">
          Showing up to 500 snapshots from <code>true_odds.graded_slip_events</code>.
        </div>
      </div>
    </div>
  );
}

export default GradedSlipsPage;
